/**
 * Unified resource access for the Shop. Two impls:
 *
 *  - RunnerWallet wraps the live RunnerScene state (delegating to scene-side
 *    methods so existing telemetry / HUD updates continue to fire).
 *  - LauncherWallet wraps an in-memory snapshot on `SessionConfig` so trades
 *    made from the Launcher modify the next session's starting state.
 *
 * Both share affordability + forecast logic via the Wallet interface; the
 * scene/snapshot specifics live in the concrete classes.
 */

import { GAMEPLAY } from "../config";
import type { InventoryItem } from "../types";
import type { Resource, SkillId, WalletDelta } from "../types";

export interface Wallet {
  readonly geo: number;
  readonly hp: number;
  readonly featherCharges: number;
  readonly inventory: readonly InventoryItem[];
  isSkillUnlocked(id: SkillId): boolean;
  /** True iff the delta could be applied right now without going negative or over-cap. */
  canAfford(cost: WalletDelta): boolean;
  /** Apply a (signed) delta. Throws if it would underflow / overflow. */
  apply(delta: WalletDelta): void;
  /** Projected delta over the next N seconds (used for "projected" tier). */
  forecastDelta(seconds: number): WalletDelta;
}

/** Sum a list of Resources into a signed WalletDelta. */
export function resourcesToDelta(resources: readonly Resource[]): WalletDelta {
  const delta: WalletDelta = {};
  const addInv: { itemId: string }[] = [];
  const gainSkills: SkillId[] = [];
  for (const r of resources) {
    switch (r.kind) {
      case "geo":
        delta.geo = (delta.geo ?? 0) + r.amount;
        break;
      case "hp":
        delta.hp = (delta.hp ?? 0) + r.amount;
        break;
      case "feather":
        delta.featherCharges = (delta.featherCharges ?? 0) + r.amount;
        break;
      case "inventoryItem":
        addInv.push({ itemId: r.itemId });
        break;
      case "skill":
        gainSkills.push(r.skillId);
        break;
    }
  }
  if (addInv.length > 0) delta.inventory = { add: addInv };
  if (gainSkills.length > 0) delta.skills = { gain: gainSkills };
  return delta;
}

/** Negate every component of a delta. Used to convert a `cost` into a wallet decrement. */
export function negateDelta(delta: WalletDelta): WalletDelta {
  const out: WalletDelta = {};
  if (delta.geo !== undefined) out.geo = -delta.geo;
  if (delta.hp !== undefined) out.hp = -delta.hp;
  if (delta.featherCharges !== undefined) out.featherCharges = -delta.featherCharges;
  if (delta.inventory?.add) out.inventory = { remove: delta.inventory.add.map((i) => i.itemId) };
  if (delta.skills?.gain) out.skills = { lose: delta.skills.gain };
  return out;
}

// -----------------------------------------------------------------------------
// RunnerWallet — backed by the live RunnerScene state.
// -----------------------------------------------------------------------------

/**
 * Methods the RunnerScene exposes so RunnerWallet can read + mutate its state
 * without RunnerWallet importing the scene class (avoids a circular dep).
 */
export interface RunnerWalletHost {
  getGeo(): number;
  addGeo(delta: number, source: string): void;
  getHp(): number;
  getMaxHp(): number;
  setHp(value: number, source: string): void;
  getFeatherCharges(): number;
  setFeatherCharges(value: number, source: string): void;
  getInventory(): readonly InventoryItem[];
  addInventoryItem(itemId: string, source: string): boolean;
  removeInventoryItem(itemId: string, source: string): boolean;
  isSkillUnlocked(id: SkillId): boolean;
  relockSkill(id: SkillId, source: "shop"): void;
  reunlockSkill(id: SkillId, source: "shop" | "geo_threshold" | "pickup"): void;
}

/**
 * Tracks recent per-resource pickup rates so RunnerWallet can answer
 * `forecastDelta(seconds)` without needing a flow_index pipeline.
 */
export class WalletIncomeTracker {
  private geoBuckets: number[] = [];
  private hpBuckets: number[] = [];
  private featherBuckets: number[] = [];
  private currentBucketMs = 0;
  private readonly bucketMs: number;
  private readonly windowSec: number;

  constructor(
    bucketMs: number = GAMEPLAY.walletForecastBucketMs,
    windowSec: number = GAMEPLAY.walletForecastWindowSec
  ) {
    this.bucketMs = bucketMs;
    this.windowSec = windowSec;
    const slots = Math.ceil((windowSec * 1000) / bucketMs);
    this.geoBuckets = new Array(slots).fill(0);
    this.hpBuckets = new Array(slots).fill(0);
    this.featherBuckets = new Array(slots).fill(0);
  }

  tick(nowMs: number): void {
    const bucketIdx = Math.floor(nowMs / this.bucketMs);
    if (bucketIdx !== this.currentBucketMs) {
      const stepsAhead = Math.min(bucketIdx - this.currentBucketMs, this.geoBuckets.length);
      for (let i = 0; i < stepsAhead; i++) {
        this.geoBuckets.shift();
        this.geoBuckets.push(0);
        this.hpBuckets.shift();
        this.hpBuckets.push(0);
        this.featherBuckets.shift();
        this.featherBuckets.push(0);
      }
      this.currentBucketMs = bucketIdx;
    }
  }

  recordGeo(amount: number): void {
    this.geoBuckets[this.geoBuckets.length - 1]! += amount;
  }
  recordHp(amount: number): void {
    this.hpBuckets[this.hpBuckets.length - 1]! += amount;
  }
  recordFeather(amount: number): void {
    this.featherBuckets[this.featherBuckets.length - 1]! += amount;
  }

  forecast(seconds: number): WalletDelta {
    const geoSum = this.geoBuckets.reduce((a, b) => a + b, 0);
    const hpSum = this.hpBuckets.reduce((a, b) => a + b, 0);
    const featherSum = this.featherBuckets.reduce((a, b) => a + b, 0);
    const scale = seconds / this.windowSec;
    return {
      geo: Math.floor(geoSum * scale),
      hp: Math.floor(hpSum * scale),
      featherCharges: Math.floor(featherSum * scale),
    };
  }
}

export class RunnerWallet implements Wallet {
  constructor(private readonly host: RunnerWalletHost, private readonly income: WalletIncomeTracker) {}

  get geo(): number {
    return this.host.getGeo();
  }
  get hp(): number {
    return this.host.getHp();
  }
  get featherCharges(): number {
    return this.host.getFeatherCharges();
  }
  get inventory(): readonly InventoryItem[] {
    return this.host.getInventory();
  }

  isSkillUnlocked(id: SkillId): boolean {
    return this.host.isSkillUnlocked(id);
  }

  canAfford(cost: WalletDelta): boolean {
    return canAffordAgainst(this, cost, this.host.getMaxHp(), GAMEPLAY.featherMaxCharges, GAMEPLAY.inventorySlots);
  }

  apply(delta: WalletDelta): void {
    if (delta.geo) this.host.addGeo(delta.geo, "shop");
    if (delta.hp) this.host.setHp(Math.max(0, Math.min(this.host.getMaxHp(), this.hp + delta.hp)), "shop");
    if (delta.featherCharges) {
      this.host.setFeatherCharges(
        Math.max(0, Math.min(GAMEPLAY.featherMaxCharges, this.featherCharges + delta.featherCharges)),
        "shop"
      );
    }
    if (delta.inventory?.remove) {
      for (const itemId of delta.inventory.remove) this.host.removeInventoryItem(itemId, "shop");
    }
    if (delta.inventory?.add) {
      for (const item of delta.inventory.add) this.host.addInventoryItem(item.itemId, "shop");
    }
    if (delta.skills?.lose) {
      for (const id of delta.skills.lose) this.host.relockSkill(id, "shop");
    }
    if (delta.skills?.gain) {
      for (const id of delta.skills.gain) this.host.reunlockSkill(id, "shop");
    }
  }

  forecastDelta(seconds: number): WalletDelta {
    return this.income.forecast(seconds);
  }
}

// -----------------------------------------------------------------------------
// LauncherWallet — in-memory snapshot used between sessions.
// -----------------------------------------------------------------------------

/** Mutable wallet snapshot carried on SessionConfig for cross-session trades. */
export interface WalletSnapshot {
  geo: number;
  hp: number;
  featherCharges: number;
  inventory: InventoryItem[];
  skills: { doubleJump: boolean; escapeFeather: boolean };
}

export function makeDefaultWalletSnapshot(): WalletSnapshot {
  return {
    geo: 0,
    hp: GAMEPLAY.heroMaxHp,
    featherCharges: 0,
    inventory: [],
    skills: { doubleJump: false, escapeFeather: false },
  };
}

export class LauncherWallet implements Wallet {
  constructor(private readonly snapshot: WalletSnapshot) {}

  get geo(): number {
    return this.snapshot.geo;
  }
  get hp(): number {
    return this.snapshot.hp;
  }
  get featherCharges(): number {
    return this.snapshot.featherCharges;
  }
  get inventory(): readonly InventoryItem[] {
    return this.snapshot.inventory;
  }

  isSkillUnlocked(id: SkillId): boolean {
    return this.snapshot.skills[id];
  }

  canAfford(cost: WalletDelta): boolean {
    return canAffordAgainst(this, cost, GAMEPLAY.heroMaxHp, GAMEPLAY.featherMaxCharges, GAMEPLAY.inventorySlots);
  }

  apply(delta: WalletDelta): void {
    if (delta.geo) this.snapshot.geo = Math.max(0, this.snapshot.geo + delta.geo);
    if (delta.hp) {
      this.snapshot.hp = Math.max(0, Math.min(GAMEPLAY.heroMaxHp, this.snapshot.hp + delta.hp));
    }
    if (delta.featherCharges) {
      this.snapshot.featherCharges = Math.max(
        0,
        Math.min(GAMEPLAY.featherMaxCharges, this.snapshot.featherCharges + delta.featherCharges)
      );
    }
    if (delta.inventory?.remove) {
      for (const itemId of delta.inventory.remove) {
        const idx = this.snapshot.inventory.findIndex((it) => it.kind === itemId);
        if (idx >= 0) this.snapshot.inventory.splice(idx, 1);
      }
    }
    if (delta.inventory?.add) {
      for (const item of delta.inventory.add) {
        if (this.snapshot.inventory.length < GAMEPLAY.inventorySlots) {
          this.snapshot.inventory.push({ kind: item.itemId as InventoryItem["kind"], pickedUpAtMs: 0 });
        }
      }
    }
    if (delta.skills?.lose) {
      for (const id of delta.skills.lose) this.snapshot.skills[id] = false;
    }
    if (delta.skills?.gain) {
      for (const id of delta.skills.gain) this.snapshot.skills[id] = true;
    }
  }

  forecastDelta(_seconds: number): WalletDelta {
    return { geo: 0, hp: 0, featherCharges: 0 };
  }
}

// -----------------------------------------------------------------------------
// Affordability check (shared).
// -----------------------------------------------------------------------------

function canAffordAgainst(
  wallet: Wallet,
  cost: WalletDelta,
  maxHp: number,
  maxFeather: number,
  maxInventory: number
): boolean {
  // `cost` is a positive-magnitude delta meaning "what the player gives up".
  // We check geo/hp/feather decrements against current values, inventory removals
  // against owned items, and skill losses against unlocked skills.
  if (cost.geo !== undefined && wallet.geo < cost.geo) return false;
  if (cost.hp !== undefined && wallet.hp < cost.hp) return false;
  if (cost.featherCharges !== undefined && wallet.featherCharges < cost.featherCharges) return false;
  if (cost.inventory?.add) {
    // "Add to player" — must fit in remaining slots.
    if (wallet.inventory.length + cost.inventory.add.length > maxInventory) return false;
  }
  if (cost.inventory?.remove) {
    const remaining = [...wallet.inventory.map((i) => i.kind)];
    for (const itemId of cost.inventory.remove) {
      const idx = remaining.indexOf(itemId as InventoryItem["kind"]);
      if (idx < 0) return false;
      remaining.splice(idx, 1);
    }
  }
  if (cost.skills?.lose) {
    for (const id of cost.skills.lose) {
      if (!wallet.isSkillUnlocked(id)) return false;
    }
  }
  if (cost.skills?.gain) {
    // Receiving a skill is always OK (no max), but reject if already owned.
    for (const id of cost.skills.gain) {
      if (wallet.isSkillUnlocked(id)) return false;
    }
  }
  // Caps on incoming positive deltas (don't let buying overflow).
  if (cost.hp !== undefined && cost.hp < 0 && wallet.hp + Math.abs(cost.hp) > maxHp) {
    // Receiving hp that would overflow — soft-clamp acceptable, not a fail.
  }
  if (cost.featherCharges !== undefined && cost.featherCharges < 0 && wallet.featherCharges + Math.abs(cost.featherCharges) > maxFeather) {
    // Receiving feathers — overflow clamps, doesn't fail.
  }
  return true;
}

/**
 * Top-level helper used by ShopScene: combine cost + receive into a single
 * canAfford check.
 */
export function offerIsAffordable(wallet: Wallet, cost: WalletDelta, receive: WalletDelta): boolean {
  // cost is positive-magnitude (what player gives); receive is positive-magnitude (what player gets).
  // Both must be representable.
  if (!wallet.canAfford(cost)) return false;
  // Receive-side: only block if the player would gain a skill they already own.
  if (receive.skills?.gain) {
    for (const id of receive.skills.gain) {
      if (wallet.isSkillUnlocked(id)) return false;
    }
  }
  return true;
}
